import React from "react"
import { Field as FieldPrimitive } from "@base-ui/react/field"
import { cn } from "../utils"
import { useFieldContext } from "./field-context"

// =============================================================================
// Styles
// =============================================================================
const labelStyles = [
  "font-sans text-sm leading-6 font-medium", // typography
  "text-gray-900" // grayed for an option by the field or the item
]

// =============================================================================
// Props
// =============================================================================
export type FieldLabelProps = {
  /** Label text */
  children?: React.ReactNode
  /** Additional CSS classes for layout */
  className?: string
}

// =============================================================================
// Component
// =============================================================================
/**
 * The label for the control inside a `Field`.
 *
 * There is no `htmlFor` to pass: the label takes the id the control registered
 * with the surrounding `Field`, which is what gives the control its accessible
 * name and makes clicking the label operate it.
 *
 * Needs a control that registers itself with the field — anything built on
 * Base UI does. Pair it with a raw `<input>` and the `for` points at an id
 * nothing owns, leaving the control with no accessible name and no warning.
 */
export const FieldLabel = React.forwardRef<HTMLLabelElement, FieldLabelProps>(
  function FieldLabel({ children, className }, ref) {
    const { labelId } = useFieldContext()

    return (
      <FieldPrimitive.Label
        ref={ref}
        id={labelId}
        data-slot="field-label"
        className={cn(labelStyles, className)}
      >
        {children}
      </FieldPrimitive.Label>
    )
  }
)

FieldLabel.displayName = "Field.Label"
